import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { ProjectImage } from "../types/project";

interface LightboxProps {
  images: ProjectImage[];
  index: number;
  onClose: () => void;
  onStep: (delta: number) => void;
}

const CONTROL =
  "grid h-11 place-items-center border border-zinc-700 font-mono text-lg text-zinc-400 transition-colors hover:border-zinc-100 hover:bg-zinc-100 hover:text-black focus-visible:border-zinc-100 focus-visible:bg-zinc-100 focus-visible:text-black focus-visible:outline-none sm:h-16";

export function Lightbox({ images, index, onClose, onStep }: LightboxProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const shot = images[index];

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        onStep(1);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        onStep(-1);
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onStep]);

  const stop = useCallback((event: { stopPropagation: () => void }) => {
    event.stopPropagation();
  }, []);

  if (!shot) {
    return null;
  }

  const counter = `SHOT ${String(index + 1).padStart(2, "0")} / ${String(
    images.length,
  ).padStart(2, "0")}`;

  const overlay = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Gallery viewer"
      onClick={onClose}
      className="absolute inset-0 z-60 grid cursor-zoom-out grid-cols-[minmax(0,1fr)] grid-rows-[44px_minmax(0,1fr)_auto] bg-[#030303]/95"
    >
      <div className="flex items-center justify-between border-b border-zinc-700/80 bg-[#070707] px-4 font-mono text-[9px] tracking-[0.18em] text-zinc-500">
        <span>{counter}</span>
        <button
          ref={closeRef}
          type="button"
          onClick={(event) => {
            stop(event);
            onClose();
          }}
          aria-label="Close viewer"
          className="cursor-pointer border border-zinc-700 px-2.5 py-1.5 font-mono text-[9px] tracking-[0.16em] text-zinc-400 transition-colors hover:border-zinc-100 hover:bg-zinc-100 hover:text-black focus-visible:border-zinc-100 focus-visible:bg-zinc-100 focus-visible:text-black focus-visible:outline-none"
        >
          CLOSE ✕
        </button>
      </div>

      <div className="grid min-h-0 w-full min-w-0 grid-cols-[44px_minmax(0,1fr)_44px] items-center gap-3 px-3 py-6 sm:grid-cols-[64px_minmax(0,1fr)_64px]">
        <button
          type="button"
          aria-label="Previous image"
          disabled={images.length < 2}
          onClick={(event) => {
            stop(event);
            onStep(-1);
          }}
          className={`${CONTROL} cursor-pointer disabled:pointer-events-none disabled:opacity-30`}
        >
          ←
        </button>

        <img
          src={shot.src}
          alt={shot.alt}
          onClick={stop}
          decoding="async"
          className="mx-auto block max-h-full max-w-full cursor-default border border-zinc-800 bg-black object-contain"
        />

        <button
          type="button"
          aria-label="Next image"
          disabled={images.length < 2}
          onClick={(event) => {
            stop(event);
            onStep(1);
          }}
          className={`${CONTROL} cursor-pointer disabled:pointer-events-none disabled:opacity-30`}
        >
          →
        </button>
      </div>

      <div className="flex items-center justify-between gap-4 border-t border-zinc-700/80 bg-[#070707] px-4 py-2.5 font-mono text-[9px] tracking-[0.12em] text-zinc-500">
        <span className="truncate">{shot.alt}</span>
        <span className="hidden whitespace-nowrap text-zinc-700 sm:block">
          ← → NAVIGATE · ESC CLOSE
        </span>
      </div>
    </div>
  );

  const host =
    typeof document === "undefined"
      ? null
      : (document.getElementById("app-frame") ?? document.body);

  return host ? createPortal(overlay, host) : overlay;
}
